/**
 * The homepage lockup flies into the header, and stays there.
 *
 * On `myrave.io` the brand opens big and centred with the promise under it,
 * and as you scroll past it, it travels up into the header and settles into
 * the place it will occupy for the rest of the visit. The point is that it is
 * the SAME mark the whole way: the header's brand is not a second copy that
 * fades in while the first fades out, it is where the first one went.
 *
 * ---
 *
 * **How, and why not the obvious way.**
 *
 * The obvious way is a crossfade — hero fades out, header fades in — and it
 * always reads as two marks, because for a moment there are two. This renders
 * ONE. Two hidden placeholders reserve the space at each end (the big one in
 * the page, the small one in the header) and a single fixed element is
 * interpolated between their measured rectangles. Hidden elements still take
 * part in layout, so both can be measured at any time and neither can drift
 * out of step with the flying copy.
 *
 * Progress is pure scroll position, not a timer or an `IntersectionObserver`:
 * scrubbing back up has to reverse it exactly, and a person flicking a phone
 * list hard enough to overshoot must not be able to strand it half-way. It is
 * `scrollY / span` clamped, where `span` is the distance the big lockup has
 * to travel — measured once per layout, not per frame.
 *
 * **At rest the real header lockup is what is on screen.** At the end of the
 * flight the flying copy is hidden and the header's own is revealed: they are
 * pixel-identical at that point, so the swap cannot be seen, and it means the
 * thing you can click and the thing a screen reader finds is the real link in
 * the real header rather than a fixed decoration floating over the page.
 *
 * **It degrades to nothing.** No hero registered (any public page that is not
 * the homepage), reduced motion asked for, or JavaScript never running: the
 * header renders its ordinary lockup and the page renders an ordinary big
 * one, both visible, and nothing flies. That is the whole fallback — there is
 * no state in which the brand is missing from both places.
 */
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect,
  useMemo, useRef, useState, type ReactNode,
} from 'react';
import { BrandLockup } from '@/ui';

/**
 * What flies, drawn at a given size: the MyRave lockup by default, or a
 * rave's own logo on its landing page (the user, 2026-10-14: "on the rave
 * page the big logo is the rave's"). The same function draws the big one in
 * the page, the small one in the header and the copy in flight, so the three
 * agree to the pixel.
 */
export type BrandArt = (size: number) => ReactNode;

const lockupArt: BrandArt = (size) => <BrandLockup size={size} />;

/**
 * Which flight an end belongs to. A page may fly more than one mark at once
 * (the user, 2026-10-15: the rave page's "Join [rave] @ [MyRave.io]" — the
 * rave's logo to the header's rave slot, MyRave's to its brand slot). Every
 * flight moves on the SAME progress, so they land together.
 */
export type FlightKey = string;
const BRAND: FlightKey = 'brand';

interface Flight {
  /** Registers a page's big mark for `key`. Null to unregister. */
  heroRef: (key: FlightKey, el: HTMLElement | null) => void;
  /** What that big mark draws — and so what flies. */
  setArt: (key: FlightKey, art: BrandArt | null) => void;
  /** Registers the header's own mark for `key`. */
  slotRef: (key: FlightKey, el: HTMLElement | null) => void;
  /** Is this flight actually happening — both ends known, motion allowed? */
  flying: (key: FlightKey) => boolean;
  /** 0 at the top of the page, 1 once everything has landed in the header. */
  progress: number;
}

const Ctx = createContext<Flight>({
  heroRef: () => {}, setArt: () => {}, slotRef: () => {}, flying: () => false, progress: 1,
});

export function useBrandFlight(): Flight {
  return useContext(Ctx);
}

/**
 * The homepage lockup's size, in pixels, from the viewport.
 *
 * A NUMBER, not a CSS clamp: `BrandLockup` sizes its mark and its wordmark
 * from one, and the flying copy and the placeholder have to agree to the
 * pixel or the landing is visibly off. One function, both ends.
 */
function heroSizeFor(width: number): number {
  return Math.round(Math.min(104, Math.max(52, width * 0.13)));
}

function useHeroSize(): number {
  const [size, setSize] = useState(() =>
    heroSizeFor(typeof window === 'undefined' ? 390 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setSize(heroSizeFor(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

type Ends = Readonly<Record<FlightKey, HTMLElement>>;

/** One registry setter: unchanged state when nothing changed, so a ref callback cannot loop. */
function register(set: (f: (prev: Ends) => Ends) => void) {
  return (key: FlightKey, el: HTMLElement | null) => set((prev) => {
    if ((prev[key] ?? null) === el) return prev;
    const next = { ...prev };
    if (el) next[key] = el; else delete next[key];
    return next;
  });
}

export function BrandFlightProvider({ children }: { children: ReactNode }) {
  const heroSize = useHeroSize();
  const [heroes, setHeroes] = useState<Ends>({});
  const [slots, setSlots] = useState<Ends>({});
  // Functions in state are stored through the updater form, never called by it.
  const [arts, setArts] = useState<Readonly<Record<FlightKey, BrandArt>>>({});
  const heroRef = useMemo(() => register(setHeroes), []);
  const slotRef = useMemo(() => register(setSlots), []);
  const setArt = useCallback((key: FlightKey, a: BrandArt | null) => setArts((prev) => {
    if ((prev[key] ?? null) === a) return prev;
    const next = { ...prev };
    if (a) next[key] = a; else delete next[key];
    return next;
  }), []);
  const [progress, setProgress] = useState(1);
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const flies = useRef<Record<FlightKey, HTMLDivElement | null>>({});
  const span = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /** The flights with both ends on the page — none when less motion was asked for. */
  const keys = useMemo(
    () => (reduced ? [] : Object.keys(heroes).filter((k) => slots[k]).sort()),
    [reduced, heroes, slots],
  );

  /*
   * `span` is how far the page has to scroll before the LOWEST big mark would
   * be sitting where its header mark is — measured from the document, so it
   * does not change as you scroll and the progress it feeds cannot drift. One
   * shared span: every flight lands on the same frame.
   *
   * Re-measured on resize AND when an element changes, because the hero
   * size is viewport-relative: a rotation changes both ends at once.
   */
  const measure = useCallback(() => {
    let most = 1;
    for (const k of keys) {
      const heroDocTop = heroes[k]!.getBoundingClientRect().top + window.scrollY;
      most = Math.max(most, heroDocTop - slots[k]!.getBoundingClientRect().top);
    }
    span.current = most;
  }, [keys, heroes, slots]);

  useLayoutEffect(() => {
    if (keys.length === 0) { setProgress(1); return; }
    measure();

    let queued = false;
    let frame = 0;
    const paint = () => {
      const p = Math.min(1, Math.max(0, window.scrollY / span.current));
      for (const k of keys) {
        const el = flies.current[k];
        if (!el) continue;
        const h = heroes[k]!.getBoundingClientRect();
        const s = slots[k]!.getBoundingClientRect();
        // Guard against a zero-width measure — a font still loading, or the
        // header shedding its lockup at a narrow width.
        const scale = h.width > 0 ? 1 + (s.width / h.width - 1) * p : 1;
        const x = h.left + (s.left - h.left) * p;
        const y = h.top + (s.top - h.top) * p;
        el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      }
      setProgress(p);
    };

    /*
     * One paint per frame. A scroll handler that writes styles on every event
     * runs many times per frame on a trackpad and reads layout in between,
     * which is the thrash this avoids.
     */
    const onScroll = () => {
      if (queued) return;
      queued = true;
      // The flag is raised BEFORE the frame is asked for and lowered inside
      // it, so this does not depend on which of the two happens first. The
      // other way round — storing the handle and clearing it in the callback
      // — silently stops throttling anything the moment a frame runs
      // synchronously, and then never schedules again.
      frame = requestAnimationFrame(() => { queued = false; paint(); });
    };
    const onResize = () => { measure(); onScroll(); };

    paint();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // The hero is sized in viewport units and the wordmark is text: both
    // settle after first paint, and a stale `span` would land it off-target.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
    for (const k of keys) { ro?.observe(heroes[k]!); ro?.observe(slots[k]!); }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [keys, heroes, slots, measure]);

  const flying = useCallback((key: FlightKey) => keys.includes(key), [keys]);
  const value = useMemo<Flight>(
    () => ({ heroRef, setArt, slotRef, flying, progress }),
    [heroRef, setArt, slotRef, flying, progress],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {keys.map((k) => (
        <div
          key={k}
          ref={(el) => { flies.current[k] = el; }}
          data-brand="flight"
          data-flight={k}
          aria-hidden="true"
          /*
           * Never clickable. The real lockup in the header is the link, and
           * this must not sit over the page swallowing taps on the way past.
           */
          /*
           * `.app-flight` anchors it at the PHYSICAL top-left, deliberately
           * not `inset-inline-start`. The flight is a `translate3d` computed
           * from `getBoundingClientRect()`, which is physical in every
           * direction; anchored at the inline start it sat on the RIGHT edge
           * of a Hebrew page and then moved a further `left` pixels, off the
           * screen. Above the header (z-index 40) on the way in, and gone
           * once it has landed and the header's own has taken over.
           */
          className="app-flight"
          style={{ visibility: progress >= 1 ? 'hidden' : 'visible' }}
        >
          {(arts[k] ?? lockupArt)(heroSize)}
        </div>
      ))}
    </Ctx.Provider>
  );
}

/**
 * The header's own mark, and the place its flight lands.
 *
 * Hidden — not unmounted — for the duration of the flight, so the header's
 * layout never shifts and the target can always be measured.
 */
export function HeaderBrand({ size = 28, art, flight = BRAND, href = '/' }: {
  size?: number; art?: BrandArt;
  /** Which flight lands here; the MyRave lockup's by default. */
  flight?: FlightKey;
  /** The default lockup's link. */
  href?: string;
}) {
  const { slotRef, flying, progress } = useBrandFlight();
  const ref = useCallback((el: HTMLElement | null) => slotRef(flight, el), [slotRef, flight]);
  return (
    <span
      ref={ref}
      data-brand="header"
      data-flight={flight}
      className="ui-inline"
      style={{ visibility: flying(flight) && progress < 1 ? 'hidden' : 'visible' }}
    >
      {art ? art(size) : <BrandLockup size={size} href={href} />}
    </span>
  );
}

/**
 * The homepage's big lockup, with the promise under it — or, `inline`, the
 * mark alone, to sit inside a line of words (the rave page's "Join … @ …").
 *
 * The mark itself goes hidden the moment a flight is possible — the flying
 * copy stands in for it from the first frame, so there is never a swap to
 * see — but it keeps its space, which is what the whole measurement rests on.
 */
export function HeroBrand({ tagline, art, flight = BRAND, inline }: {
  tagline?: string; art?: BrandArt; flight?: FlightKey; inline?: boolean;
}) {
  const { heroRef, setArt, flying } = useBrandFlight();
  const heroSize = useHeroSize();
  const ref = useCallback((el: HTMLElement | null) => heroRef(flight, el), [heroRef, flight]);
  // The flying copy draws what this hero draws; gone with the hero.
  useEffect(() => { setArt(flight, art ?? null); return () => setArt(flight, null); }, [art, setArt, flight]);
  const mark = (
    <span ref={ref} data-brand="hero" data-flight={flight} className="ui-inline"
      style={{ visibility: flying(flight) ? 'hidden' : 'visible' }}>
      {(art ?? lockupArt)(heroSize)}
    </span>
  );
  if (inline) return mark;
  return (
    <div className="app-hero">
      {mark}
      {tagline && (
        <p className="app-hero-tagline">
          {tagline}
        </p>
      )}
    </div>
  );
}
